import apiClient from '../client';
import type {
  CarePlanResponse,
  CreateCarePlanRequest,
  UpdateCarePlanRequest,
  UpdateGoalRequest,
  UpdateActivityRequest,
} from '@/types/careplan.types';

const BASE_URL = '/v1/care-plans';

export const carePlansApi = {
  /**
   * Lista planos de cuidado de um paciente
   */
  getByPatient: async (patientId: string): Promise<CarePlanResponse[]> => {
    const response = await apiClient.get<CarePlanResponse[]>(`${BASE_URL}/patient/${patientId}`);
    return response.data;
  },

  /**
   * Lista planos de cuidado ativos de um paciente
   */
  getActiveByPatient: async (patientId: string): Promise<CarePlanResponse[]> => {
    const response = await apiClient.get<CarePlanResponse[]>(`${BASE_URL}/patient/${patientId}/active`);
    return response.data;
  },

  /**
   * Busca plano de cuidado por ID
   */
  getById: async (id: string): Promise<CarePlanResponse> => {
    const response = await apiClient.get<CarePlanResponse>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Cria novo plano de cuidado
   */
  create: async (data: CreateCarePlanRequest): Promise<CarePlanResponse> => {
    const response = await apiClient.post<CarePlanResponse>(BASE_URL, data);
    return response.data;
  },

  /**
   * Atualiza plano de cuidado existente
   */
  update: async (id: string, data: UpdateCarePlanRequest): Promise<CarePlanResponse> => {
    const response = await apiClient.put<CarePlanResponse>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Ativa um plano de cuidado (muda status de DRAFT para ACTIVE)
   */
  activate: async (id: string): Promise<CarePlanResponse> => {
    const response = await apiClient.post<CarePlanResponse>(`${BASE_URL}/${id}/activate`);
    return response.data;
  },

  /**
   * Marca plano de cuidado como concluído
   */
  complete: async (id: string): Promise<CarePlanResponse> => {
    const response = await apiClient.post<CarePlanResponse>(`${BASE_URL}/${id}/complete`);
    return response.data;
  },

  /**
   * Cancela plano de cuidado
   */
  cancel: async (id: string, reason?: string): Promise<CarePlanResponse> => {
    const response = await apiClient.post<CarePlanResponse>(`${BASE_URL}/${id}/cancel`, null, {
      params: reason ? { reason } : undefined,
    });
    return response.data;
  },

  /**
   * Exclui plano de cuidado (apenas rascunhos)
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Atualiza uma meta individual do plano de cuidado
   */
  updateGoal: async (id: string, data: UpdateGoalRequest): Promise<CarePlanResponse> => {
    const response = await apiClient.put<CarePlanResponse>(`${BASE_URL}/${id}/goals`, data);
    return response.data;
  },

  /**
   * Atualiza uma atividade individual do plano de cuidado
   */
  updateActivity: async (id: string, data: UpdateActivityRequest): Promise<CarePlanResponse> => {
    const response = await apiClient.put<CarePlanResponse>(`${BASE_URL}/${id}/activities`, data);
    return response.data;
  },
};
